import { useEffect, useMemo } from 'react';
import {
  AccountDetail,
  AuthDriverList,
  AuthDriversInput,
  ChargePointAction,
  ChargePointStatus,
  InputMaybe,
  LinkedPerson,
  ManagePerRelationshipInput,
  ManagePerRelationshipResponse,
  Maybe,
  PremiseInfo,
} from '../../__generated__/pge-types';
import useAuthQuery from '../../hooks/useAuthQuery';
import { getChargePointDetails, managePerRelationship } from './queries';
import useFormState from '../../hooks/useFormState';
import { Model } from './chargePointForm.rules';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import apolloClient from '../../lib/apolloClient';
import ROUTES from '../../routes';
import { navigate } from 'gatsby';
import { usePersonManage } from '../../hooks/usePersonManage';
import { getActiveEncryptedSaId } from '../my-programs/utils';
import { getAuthDriverIQBDStatus, getIQBDEligibility } from '../iqbd/queries';
import useSelectedAccountParams from '../../hooks/useSelectedAccountParams';
import { GET_ACCOUNT_DETAILS } from '../my-programs/queries';

export default () => {
  const {
    personList,
    refetchPerson,
    customer,
    mainPersonEmail,
  } = usePersonManage();
  const { wrapWithLoader } = useWrapWithLoader();

  const { accountParams } = useSelectedAccountParams();
  const {
    data: account,
    loading: accountsLoading,
    error: getAccountsError,
  } = useAuthQuery<{
    getAccountDetails: [AccountDetail];
  }>(GET_ACCOUNT_DETAILS, {
    variables: {
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !accountParams,
    errorPolicy: 'all',
  });

  const encryptedSAId = getActiveEncryptedSaId(
    account?.getAccountDetails?.[0]?.premiseInfo as PremiseInfo[] | undefined,
  );
  const accountPremiseID =
    account?.getAccountDetails?.[0].premiseInfo?.[0]?.encryptedPremiseId;
  const {
    data: iqbdEligibilityDetail,
    loading: iqbdEligibilityLoading,
  } = useAuthQuery<{ getAccountDetails: AccountDetail[] }>(getIQBDEligibility, {
    variables: {
      iqbdParams: {
        encryptedSaIds: encryptedSAId,
      },
      params: {
        accountNumberList: [accountParams],
      },
    },
    skip: !(encryptedSAId && accountParams),
    fetchPolicy: 'no-cache',
  });

  const isIqbdAccount =
    iqbdEligibilityDetail?.getAccountDetails[0].IQBDStatus?.isEnrolled;

  const loading = accountsLoading || iqbdEligibilityLoading;

  const { data: chargePointData, loading: dataLoading } = useAuthQuery<{
    getChargePointPersons: ManagePerRelationshipResponse;
  }>(getChargePointDetails, {
    variables: {
      params: {
        action: ChargePointAction.Read,
        accountId: accountParams?.accountNumber,
        encryptedParentPerId: accountParams?.encryptedPersonId,
        encryptedPremiseId: accountPremiseID || '',
      },
    },
    skip: !(encryptedSAId && accountParams),
    fetchPolicy: 'no-cache',
  });
  const chargePoint = chargePointData;
  const form = useFormState(
    getDefaultFormValues(chargePoint?.getChargePointPersons),
    {
      //  validate: validationChargepointRules
    },
  );
  function getCurrentDriverDetails(
    chargePointDetails: ManagePerRelationshipResponse | undefined,
    currentDriverPosition: number,
  ) {
    const currentDriverChargePointDetails =
      chargePointDetails &&
      chargePointDetails?.authDriverList &&
      chargePointDetails?.authDriverList?.length >= currentDriverPosition
        ? chargePointDetails?.authDriverList?.[currentDriverPosition]
        : null;
    const isAuthDriver =
      currentDriverChargePointDetails?.opConnectStatus &&
      (currentDriverChargePointDetails?.opConnectStatus ===
        ChargePointStatus.Approved ||
        currentDriverChargePointDetails?.opConnectStatus ===
          ChargePointStatus.Pending);
    return isAuthDriver ? currentDriverChargePointDetails : {};
  }

  useEffect(() => {
    if (chargePointData) {
      const chargePointDetails = chargePointData?.getChargePointPersons;
      const newData = { ...chargePointDetails };
      const authDriverList = newData.authDriverList?.filter(
        (driver: Maybe<AuthDriverList>) =>
          driver?.opConnectStatus === ChargePointStatus.Approved ||
          driver?.opConnectStatus === ChargePointStatus.Pending,
      );
      newData.authDriverList = authDriverList;
      const primaryDriverDetails = getCurrentDriverDetails(newData, 0);
      const secondaryDriverDetails = getCurrentDriverDetails(newData, 1);
      const thirdDriverDetails = getCurrentDriverDetails(newData, 2);

      void form.setValue(
        'isPrimaryDriverOpConnectEmail',
        primaryDriverDetails &&
          primaryDriverDetails?.isOpConnectEmailAvailable!,
      );
      void form.setValue(
        'primeDriverOpConnectEmail',
        primaryDriverDetails?.opConnectEmail &&
          primaryDriverDetails?.opConnectEmail,
      );
      void form.setValue('primeDriver', primaryDriverDetails?.personId);
      void form.setValue(
        'primeDriverAccessCode',
        primaryDriverDetails?.accessCode,
      );
      void form.setValue(
        'primeDriverStatus',
        primaryDriverDetails?.chargePointstatus,
      );
      void form.setValue(
        'primeDriverOpconnectStatus',
        primaryDriverDetails?.opConnectStatus,
      );
      void form.setValue(
        'secondaryDriverAccessCode',
        secondaryDriverDetails?.accessCode,
      );
      void form.setValue(
        'secondaryDriverOpconnectStatus',
        secondaryDriverDetails?.opConnectStatus,
      );
      void form.setValue(
        'secondaryDriverStatus',
        secondaryDriverDetails?.chargePointstatus,
      );
      void form.setValue('secondaryDriver', secondaryDriverDetails?.personId);
      void form.setValue(
        'isSecondaryDriverOpconnectEmail',
        secondaryDriverDetails &&
          secondaryDriverDetails?.isOpConnectEmailAvailable!,
      );
      void form.setValue(
        'secondaryDriverOpConnectEmail',
        secondaryDriverDetails?.opConnectEmail &&
          secondaryDriverDetails?.opConnectEmail,
      );
      void form.setValue('thirdDriver', thirdDriverDetails?.personId);
      void form.setValue(
        'thirdDriverAccessCode',
        thirdDriverDetails?.accessCode,
      );
      void form.setValue(
        'thirdDriverOpconnectStatus',
        thirdDriverDetails?.opConnectStatus,
      );
      void form.setValue(
        'thirdDriverStatus',
        thirdDriverDetails?.chargePointstatus,
      );
      void form.setValue(
        'thirdDriverOpConnectEmail',
        thirdDriverDetails?.opConnectEmail &&
          thirdDriverDetails?.opConnectEmail,
      );
      void form.setValue(
        'isThirdDriverOpconnectEmail',
        thirdDriverDetails && thirdDriverDetails?.isOpConnectEmailAvailable!,
      );
    }
  }, [chargePointData]);

  const defaultFormValues = useMemo(
    () => getDefaultFormValues(chargePointData?.getChargePointPersons),
    [chargePointData],
  );

  function getDefaultFormValues(
    chargePointDetails?: ManagePerRelationshipResponse,
  ): Model {
    const newData = { ...chargePointDetails };
    const authDriverList = newData.authDriverList?.filter(
      (driver: Maybe<AuthDriverList>) =>
        driver?.opConnectStatus === ChargePointStatus.Approved ||
        driver?.opConnectStatus === ChargePointStatus.Pending,
    );
    newData.authDriverList = authDriverList;
    const primaryDriverDetails = getCurrentDriverDetails(newData, 0);
    const secondaryDriverDetails = getCurrentDriverDetails(newData, 1);
    const thirdDriverDetails = getCurrentDriverDetails(newData, 2);

    return {
      isPrimaryDriverOpConnectEmail: primaryDriverDetails
        ? primaryDriverDetails?.isOpConnectEmailAvailable!
        : false,
      primeDriverOpConnectEmail: primaryDriverDetails
        ? primaryDriverDetails.opConnectEmail!
        : undefined,
      isSecondaryDriverOpconnectEmail: secondaryDriverDetails
        ? secondaryDriverDetails?.isOpConnectEmailAvailable!
        : false,
      secondaryDriverOpConnectEmail: secondaryDriverDetails
        ? secondaryDriverDetails.opConnectEmail!
        : undefined,
      primeDriver: primaryDriverDetails
        ? primaryDriverDetails.personId!
        : undefined,
      primeDriverAccessCode: primaryDriverDetails
        ? primaryDriverDetails?.accessCode!
        : undefined,
      primeDriverStatus: primaryDriverDetails
        ? primaryDriverDetails?.chargePointstatus!
        : undefined,
      primeDriverOpconnectStatus: primaryDriverDetails
        ? primaryDriverDetails?.opConnectStatus!
        : undefined,
      secondaryDriver: secondaryDriverDetails
        ? secondaryDriverDetails?.personId!
        : undefined,
      secondaryDriverAccessCode: secondaryDriverDetails
        ? secondaryDriverDetails?.accessCode!
        : undefined,
      secondaryDriverStatus: secondaryDriverDetails
        ? secondaryDriverDetails?.chargePointstatus!
        : undefined,
      secondaryDriverOpconnectStatus: secondaryDriverDetails
        ? secondaryDriverDetails?.opConnectStatus!
        : undefined,
      thirdDriver: thirdDriverDetails
        ? thirdDriverDetails?.personId!
        : undefined,
      thirdDriverAccessCode: thirdDriverDetails
        ? thirdDriverDetails?.accessCode!
        : undefined,
      thirdDriverStatus: thirdDriverDetails
        ? thirdDriverDetails?.chargePointstatus!
        : undefined,
      thirdDriverOpConnectEmail: thirdDriverDetails
        ? thirdDriverDetails?.opConnectEmail!
        : undefined,
      isThirdDriverOpconnectEmail: thirdDriverDetails
        ? thirdDriverDetails?.isOpConnectEmailAvailable!
        : false,
      thirdDriverOpconnectStatus: thirdDriverDetails
        ? thirdDriverDetails?.opConnectStatus!
        : undefined,
    };
  }

  const handleDeletePerson = wrapWithLoader(
    async (
      personId: string,
      personName: string,
      email: string,
      accessCode: string,
    ) => {
      const payload: ManagePerRelationshipInput = {
        action: ChargePointAction.Update,
        accountId: accountParams?.accountNumber,
        encryptedParentPerId: accountParams?.encryptedPersonId as string,
        encryptedPremiseId: accountPremiseID as string,
        authDrivers: [
          {
            action: ChargePointAction.Update,
            personId: personId,
            name: personName,
            email: email,
            opConnectStatus: ChargePointStatus.Removed,
            chargePointStatus: ChargePointStatus.Removed,
            accessCode: accessCode,
          },
        ],
      };
      const response = await managePersonMutation(payload);
      if (response) {
        await navigate(ROUTES.CHARGEPOINTSUBMIT, {
          state: {
            isDeleted: true,
            response: response.authDriverList?.map(
              (driver: Maybe<AuthDriverList>) => {
                return {
                  ...driver,
                  personName: personName,
                };
              },
            ),
          },
        });
      }
    },
  );

  const handleSubmit = wrapWithLoader(
    async (
      primeDriver: LinkedPerson | undefined,
      secDriver: LinkedPerson | undefined,
      thirdDriver: LinkedPerson | undefined,
    ) => {
      const payload: ManagePerRelationshipInput = {
        action: ChargePointAction.Update,
        accountId: accountParams?.accountNumber,
        encryptedParentPerId: accountParams?.encryptedPersonId as string,
        encryptedPremiseId: accountPremiseID as string,
        authDrivers: [
          ...(form.values.primeDriver !== defaultFormValues.primeDriver ||
          (defaultFormValues.primeDriverOpConnectEmail !==
            form.values.primeDriverOpConnectEmail &&
            form.values.primeDriver)
            ? [
                {
                  action: defaultFormValues.primeDriver
                    ? ChargePointAction.Update
                    : ChargePointAction.Add,
                  personId: form.values.primeDriver,
                  name: primeDriver?.name,
                  email: primeDriver?.email,
                  ...(defaultFormValues.primeDriverOpConnectEmail !==
                  form.values.primeDriverOpConnectEmail
                    ? {
                        opConnectEmail:
                          form.values.primeDriverOpConnectEmail === undefined ||
                          form.values.primeDriverOpConnectEmail === ''
                            ? ''
                            : form.values.primeDriverOpConnectEmail,
                      }
                    : form.values.primeDriverOpConnectEmail === undefined ||
                      form.values.primeDriverOpConnectEmail === ''
                    ? { opConnectEmail: '' }
                    : {}),
                  chargePointStatus: (form.values.primeDriverStatus
                    ? form.values.primeDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                  opConnectStatus: (form.values.primeDriverStatus
                    ? form.values.primeDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                },
              ]
            : []),
          ...(form.values.secondaryDriver !==
            defaultFormValues.secondaryDriver ||
          (defaultFormValues.secondaryDriverOpConnectEmail !==
            form.values.secondaryDriverOpConnectEmail &&
            form.values.secondaryDriver)
            ? [
                {
                  action: defaultFormValues.secondaryDriver
                    ? ChargePointAction.Update
                    : ChargePointAction.Add,
                  personId: form.values.secondaryDriver,
                  name: secDriver?.name,
                  email: secDriver?.email,
                  ...(defaultFormValues.secondaryDriverOpConnectEmail !==
                  form.values.secondaryDriverOpConnectEmail
                    ? {
                        opConnectEmail:
                          form.values.secondaryDriverOpConnectEmail ===
                            undefined ||
                          form.values.secondaryDriverOpConnectEmail === ''
                            ? ''
                            : form.values.secondaryDriverOpConnectEmail,
                      }
                    : form.values.secondaryDriverOpConnectEmail === undefined ||
                      form.values.secondaryDriverOpConnectEmail === ''
                    ? { opConnectEmail: '' }
                    : {}),
                  chargePointStatus: (form.values.secondaryDriverStatus
                    ? form.values.secondaryDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                  opConnectStatus: (form.values.secondaryDriverStatus
                    ? form.values.secondaryDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                },
              ]
            : []),
          ...(form.values.thirdDriver !== defaultFormValues.thirdDriver ||
          (defaultFormValues.thirdDriverOpConnectEmail !==
            form.values.thirdDriverOpConnectEmail &&
            form.values.thirdDriver)
            ? [
                {
                  action: defaultFormValues.thirdDriver
                    ? ChargePointAction.Update
                    : ChargePointAction.Add,
                  personId: form.values.thirdDriver,
                  name: thirdDriver?.name,
                  email: thirdDriver?.email,
                  ...(defaultFormValues.thirdDriverOpConnectEmail !==
                  form.values.thirdDriverOpConnectEmail
                    ? {
                        opConnectEmail:
                          form.values.thirdDriverOpConnectEmail === undefined ||
                          form.values.thirdDriverOpConnectEmail === ''
                            ? ''
                            : form.values.thirdDriverOpConnectEmail,
                      }
                    : form.values.thirdDriverOpConnectEmail === undefined ||
                      form.values.thirdDriverOpConnectEmail === ''
                    ? { opConnectEmail: '' }
                    : {}),
                  chargePointStatus: (form.values.thirdDriverStatus
                    ? form.values.thirdDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                  opConnectStatus: (form.values.thirdDriverStatus
                    ? form.values.thirdDriverStatus
                    : ChargePointStatus.Pending) as ChargePointStatus,
                },
              ]
            : []),
        ],
      };

      const response = await managePersonMutation(payload);
      if (response) {
        await navigate(ROUTES.CHARGEPOINTSUBMIT, {
          state: {
            response: response.authDriverList?.map(
              (driver: Maybe<AuthDriverList>) => {
                const person = personList?.find(
                  per => per.personId === driver?.personId,
                );
                const matchedDriver = payload.authDrivers?.find(
                  (authDriver: InputMaybe<AuthDriversInput>) =>
                    authDriver?.personId === person?.personId,
                );
                const action = matchedDriver?.action;
                return {
                  ...driver,
                  personName: person ? person.name : 'Unknown',
                  action: action,
                };
              },
            ),
          },
        });
      }
    },
  );

  async function managePersonMutation(
    payload: ManagePerRelationshipInput,
  ): Promise<ManagePerRelationshipResponse> {
    const result = await apolloClient.mutate<{
      managePerRelationship: ManagePerRelationshipResponse;
    }>({
      mutation: managePerRelationship,
      variables: {
        payload,
      },
    });
    return result?.data?.managePerRelationship!;
  }
  const getIQBDStatusAuthDriver = async (personId: string) => {
    return await getIQBDStatusMutation(personId);
  };

  async function getIQBDStatusMutation(
    encryptedPersonId: string,
  ): Promise<boolean> {
    const result = await apolloClient.mutate<{
      getAuthDriverIQBDStatus: any;
    }>({
      mutation: getAuthDriverIQBDStatus,
      variables: {
        params: {
          encryptedPersonId: encryptedPersonId,
        },
      },
    });
    return result?.data?.getAuthDriverIQBDStatus?.isEligible;
  }

  return {
    form,
    defaultFormValues,
    handleSubmit,
    dataLoading,
    handleDeletePerson,
    personList,
    refetchPerson,
    customer,
    mainPersonEmail,
    isIqbdAccount,
    loading,
    getIQBDStatusAuthDriver,
    chargePoint,
  };
};
